/**
 * Deviation line between actual GPS position and V4 position (estimated).
 * Shows for selected vehicle only when toggle is enabled.
 *
 * PERFORMANCE: Uses specific selector for selected vehicle only, not entire vehicles Map.
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import type { LineLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, LineString } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { V4_POSITION_CONFIG } from '../../config';
import { haversineDistance } from '../../utils/haversine';
import { interpolatePosition } from '../../utils/interpolation';

const DEVIATION_SOURCE_ID = 'deviation-source';
const DEVIATION_LAYER_ID = 'deviation-layer';

interface DeviationLineProperties {
  id: string;
  distance: number;
  color: string;
}

const EMPTY_GEOJSON: FeatureCollection<LineString, DeviationLineProperties> = {
  type: 'FeatureCollection',
  features: [],
};

export const DeviationLine = memo(function DeviationLine() {
  // Use specific selector - only re-renders when selected vehicle changes, not on every position update
  const selectedVehicle = useVehicleStore((state) =>
    state.selectedVehicleId ? state.vehicles.get(state.selectedVehicleId) : null
  );
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const showSelectedV4Position = useSettingsStore((state) => state.showSelectedV4Position);

  // Convert selected vehicle's deviation to GeoJSON
  const geojson = useMemo(() => {
    // Early exit if no selection or toggle off
    if (!selectedVehicle || !showSelectedV4Position) {
      return EMPTY_GEOJSON;
    }

    // Must have V1-DA GPS data and V4 position available.
    // Use v1LastUpdateTime (stable) instead of data_source (flips on each update, causing flicker).
    if (!selectedVehicle.v4_position || selectedVehicle.v1LastUpdateTime === null) {
      return EMPTY_GEOJSON;
    }

    // Skip dead vehicles
    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_GEOJSON;
    }

    // Calculate distance
    const distance = haversineDistance(
      selectedVehicle.targetLatitude,
      selectedVehicle.targetLongitude,
      selectedVehicle.v4_position.latitude,
      selectedVehicle.v4_position.longitude
    );

    // Only show line if deviation exceeds threshold
    if (distance < V4_POSITION_CONFIG.MERGE_THRESHOLD_METERS) {
      return EMPTY_GEOJSON;
    }

    // Get interpolated current position for smooth line animation
    // Need current time for animation interpolation
    const now = Date.now(); // eslint-disable-line
    const [lat, lon] = interpolatePosition(
      selectedVehicle.latitude,
      selectedVehicle.longitude,
      selectedVehicle.targetLatitude,
      selectedVehicle.targetLongitude,
      selectedVehicle.lastUpdateTime,
      now
    );

    // Color based on distance (red for large deviations, orange for moderate)
    const color = distance > V4_POSITION_CONFIG.DEVIATION_WARNING_METERS ? '#ff4444' : '#ffaa00';

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [lon, lat], // GPS position (interpolated)
              [selectedVehicle.v4_position.longitude, selectedVehicle.v4_position.latitude], // V4 position
            ],
          },
          properties: {
            id: selectedVehicle.service_journey_id,
            distance,
            color,
          },
        },
      ],
    } as FeatureCollection<LineString, DeviationLineProperties>;
  }, [selectedVehicle, showSelectedV4Position]);

  // Line layer style
  const layerStyle: LineLayerSpecification = useMemo(
    () => ({
      id: DEVIATION_LAYER_ID,
      type: 'line',
      source: DEVIATION_SOURCE_ID,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.5,
      },
    }),
    []
  );

  // Don't render if toggle is off or no vehicle selected
  if (!showSelectedV4Position || !selectedVehicleId) {
    return null;
  }

  return (
    <Source id={DEVIATION_SOURCE_ID} type="geojson" data={geojson}>
      <Layer {...layerStyle} />
    </Source>
  );
});
